import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasAdminAccess } from '../../lib/admin';
import { cn } from '../../lib/utils';
import UserAvatar from '../UserAvatar';
import {
  ArrowRightStartOnRectangleIcon,
  BriefcaseIcon,
  ChevronUpDownIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  CurrencyDollarIcon,
  GiftIcon,
  MoonIcon,
  ShieldCheckIcon,
  SunIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';

/**
 * The bottom-of-sidebar profile popover — a single component that
 * both Sidebar.tsx (standard) and OrbiSidebar.tsx (Orbi surfaces) now
 * consume. Previously both sidebars carried near-identical copies of
 * this block and drifted on admin gating (fixed twice in this session)
 * and theme toggle styling.
 *
 * Owns:
 *   · Trigger button (avatar + name + chevron)
 *   · Outside-click / Escape dismiss
 *   · Menu items — Profile Settings, Portfolio Setup, Admin (gated by
 *     users.role or the hardcoded allowlist via hasAdminAccess), Billing
 *   · Log out
 *   · Theme toggle (Dark / Light / System) with localStorage persistence
 *
 * Reads user from AuthContext and resolves admin locally, so consumers
 * just render <SidebarProfilePopover /> — no props needed for the common
 * case.
 */

type ThemeMode = 'light' | 'dark' | 'system';

function applyTheme(mode: ThemeMode) {
  if (mode === 'dark') document.documentElement.classList.add('dark');
  else if (mode === 'light') document.documentElement.classList.remove('dark');
  else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }
}

interface SidebarProfilePopoverProps {
  /** Override the click handler for Log out (defaults to context logout + navigate to /login). */
  onLogout?: () => void;
  /** Extra classes on the outer container (positions the popover relative to it). */
  className?: string;
  /** Trigger visual variant: 'card' matches Orbi sidebar chrome, 'compact' matches standard. */
  triggerVariant?: 'card' | 'compact';
}

export default function SidebarProfilePopover({
  onLogout,
  className,
  triggerVariant = 'compact',
}: SidebarProfilePopoverProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth() as {
    user?: { id?: string; email?: string; name?: string; avatar_url?: string; role?: string };
    logout: () => void;
  };
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>('light');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const saved = localStorage.getItem('planbase_theme') as ThemeMode | null;
    if (saved) setTheme(saved);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const displayName = user?.name || user?.email?.split('@')[0] || 'Account';
  const isAdmin = hasAdminAccess(user?.id, user?.email, user?.role);

  const setThemeAndPersist = (mode: ThemeMode) => {
    setTheme(mode);
    localStorage.setItem('planbase_theme', mode);
    applyTheme(mode);
  };

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    setOpen(false);
    if (onLogout) onLogout();
    else {
      logout();
      navigate('/login');
    }
  };

  return (
    <div className={cn('relative', className)} ref={rootRef}>
      {open && (
        <div className="absolute bottom-full left-0 right-0 z-50 mb-2 overflow-hidden rounded-2xl border border-border bg-background shadow-xl">
          <div className="rounded-b-2xl border-b border-border bg-[#f0f0f0] px-4 py-4 dark:bg-muted/50">
            <p className="truncate text-base font-semibold leading-tight text-foreground">
              {displayName}
            </p>
            {user?.email && (
              <p className="mt-1 truncate text-xs text-muted-foreground">{user.email}</p>
            )}
          </div>

          <div className="space-y-0.5 p-1">
            <MenuItem icon={Cog6ToothIcon} label="Profile Settings" onClick={() => go('/profile/edit')} />
            <MenuItem icon={BriefcaseIcon} label="Portfolio Setup" onClick={() => go('/portfolio-setup')} />
            {isAdmin && (
              <MenuItem icon={ShieldCheckIcon} label="Admin" onClick={() => go('/admin')} />
            )}
            <MenuItem icon={CurrencyDollarIcon} label="Billing" onClick={() => go('/pricing')} />
            <MenuItem icon={GiftIcon} label="What's new" onClick={() => go('/changelog')} />

            <div className="border-t border-border" />

            <MenuItem icon={ArrowRightStartOnRectangleIcon} label="Log out" onClick={handleLogout} />

            <div className="flex items-center gap-2.5 px-3 py-2">
              <SwatchIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              <span className="flex-1 text-xs text-foreground">Theme</span>
              <div className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
                <ThemeButton mode="dark" current={theme} onClick={setThemeAndPersist} icon={MoonIcon} label="Dark" />
                <ThemeButton mode="light" current={theme} onClick={setThemeAndPersist} icon={SunIcon} label="Light" />
                <ThemeButton mode="system" current={theme} onClick={setThemeAndPersist} icon={ComputerDesktopIcon} label="System" />
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Open profile menu"
        className={cn(
          'flex w-full items-center gap-2 transition-colors hover:bg-white dark:hover:bg-muted',
          triggerVariant === 'card'
            ? 'rounded-xl border border-border py-1 pl-1 pr-2'
            : 'rounded-xl border border-border py-1 pl-1 pr-2',
        )}
      >
        <UserAvatar
          name={user?.name}
          avatarUrl={user?.avatar_url}
          size="sm"
          className="!h-5 !w-5 !text-[9px] flex-shrink-0"
        />
        <span className="flex-1 truncate text-left text-xs font-normal text-foreground">
          {displayName}
        </span>
        <ChevronUpDownIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
      </button>
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Cog6ToothIcon;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-lg border border-transparent px-3 py-2 text-left text-xs text-foreground transition-colors hover:border-border/40 hover:bg-white dark:hover:border-transparent dark:hover:bg-muted"
    >
      <Icon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
      {label}
    </button>
  );
}

function ThemeButton({
  mode,
  current,
  onClick,
  icon: Icon,
  label,
}: {
  mode: ThemeMode;
  current: ThemeMode;
  onClick: (mode: ThemeMode) => void;
  icon: typeof MoonIcon;
  label: string;
}) {
  const active = current === mode;
  return (
    <button
      type="button"
      onClick={() => onClick(mode)}
      aria-label={`${label} theme`}
      aria-pressed={active}
      className={cn(
        'rounded-md p-1.5 transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-3 w-3" />
    </button>
  );
}
